// Ring Size Validation for Infinite Options
(function() {
  'use strict';

  const RING_SIZE_ERROR_MESSAGE = 'Please select a ring size';
  const PLACEHOLDER_TEXT = 'Select Your Ring Size';
  const PLACEHOLDER_VALUE = '';
  const DEBUG = false; // Set to true to enable console logging

  let ringSizeErrorElement = null;
  let hasFiltered = false; // Track if filtering has been applied

  function log(...args) {
    if (DEBUG) console.log('[Ring Size Filter]', ...args);
  }

  // Parse allowed ring sizes from product metafield injected into window
  function getAllowedRingSizes() {
    const source = window.productRingSizeFilter && window.productRingSizeFilter.sizes;
    if (!source) {
      log('No ring size filter data found in window.productRingSizeFilter');
      return [];
    }

    let values = [];
    if (Array.isArray(source)) {
      values = source;
    } else if (typeof source === 'string') {
      // Try to parse JSON array strings first, fall back to CSV
      const trimmed = source.trim();
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            values = parsed;
          } else {
            values = [parsed];
          }
        } catch (e) {
          values = source.split(/[,;\n]/);
        }
      } else {
        values = source.split(/[,;\n]/);
      }
    } else if (typeof source === 'object' && source.value) {
      // Handle metafield object format
      values = Array.isArray(source.value) ? source.value : [source.value];
    }

    const result = values
      .map((v) => (v === null || v === undefined ? '' : v.toString().trim()))
      .filter((v) => v !== '');

    log('Parsed allowed sizes:', result);
    return result;
  }

  // Normalize size values so "7" matches "7.0", trimming and lowering
  function normalizeSize(value) {
    if (value === null || value === undefined) return '';
    const raw = value.toString().trim();
    if (raw === '') return '';
    const numeric = parseFloat(raw);
    if (!Number.isNaN(numeric)) return numeric.toString();
    return raw.toLowerCase();
  }

  // Function to find the ring size dropdown
  function getRingSizeSelect() {
    return document.querySelector('#infiniteoptions-container select[name="properties[Ring Size]"]');
  }

  // Function to get or create the error element
  function ensureErrorElement() {
    if (ringSizeErrorElement && document.body.contains(ringSizeErrorElement)) {
      return ringSizeErrorElement;
    }

    const select = getRingSizeSelect();
    if (!select) return null;

    const selectParent = select.closest('.spb-select');
    if (!selectParent) return null;

    let error = selectParent.parentNode ? selectParent.parentNode.querySelector('#ring-size-error') : null;
    if (!error) {
      error = document.createElement('div');
      error.id = 'ring-size-error';
      error.className = 'ring-size-error';
      error.style.display = 'none';
      error.setAttribute('role', 'alert');
      error.setAttribute('aria-live', 'polite');
      error.textContent = '';

      // Insert after the select parent
      if (selectParent.nextElementSibling && selectParent.parentNode) {
        selectParent.parentNode.insertBefore(error, selectParent.nextElementSibling);
      } else if (selectParent.parentNode) {
        selectParent.parentNode.appendChild(error);
      }
    }

    ringSizeErrorElement = error;
    return ringSizeErrorElement;
  }

  // Function to hide the error
  function hideError() {
    const error = ensureErrorElement();
    if (error) {
      error.style.display = 'none';
    }

    const select = getRingSizeSelect();
    if (select) {
      const selectParent = select.closest('.spb-select');
      if (selectParent) {
        selectParent.classList.remove('ring-size-error');
        select.removeAttribute('aria-invalid');
      }
    }
  }

  // Function to show the error
  function showError(message) {
    const error = ensureErrorElement();
    if (!error) return;

    error.textContent = message;
    error.style.display = 'block';

    const select = getRingSizeSelect();
    if (select) {
      const selectParent = select.closest('.spb-select');
      if (selectParent) {
        selectParent.classList.add('ring-size-error');
        select.setAttribute('aria-invalid', 'true');
      }

      // Scroll to the error
      select.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Function to check if a valid ring size is selected
  function isRingSizeSelected() {
    const select = getRingSizeSelect();
    if (!select) return true; // If no select, don't block submission

    const value = select.value;
    return value && value !== PLACEHOLDER_VALUE && value.trim() !== '';
  }

  // Function to add the placeholder option
  function addPlaceholderOption() {
    const select = getRingSizeSelect();
    if (!select) return;

    // Check if placeholder already exists
    const existingPlaceholder = select.querySelector(`option[value="${PLACEHOLDER_VALUE}"]`);
    if (existingPlaceholder) return;

    // Create the placeholder option
    const placeholder = document.createElement('option');
    placeholder.value = PLACEHOLDER_VALUE;
    placeholder.textContent = PLACEHOLDER_TEXT;
    placeholder.disabled = true;
    placeholder.selected = true;

    // Insert at the beginning
    select.insertBefore(placeholder, select.firstChild);

    // Set the select to show the placeholder
    select.value = PLACEHOLDER_VALUE;

    // Mark select as invalid initially (for styling)
    select.setAttribute('required', 'required');
  }

  // Limit dropdown options to allowed sizes from metafield
  function filterRingSizeOptions() {
    const select = getRingSizeSelect();
    if (!select) {
      log('Ring size select not found for filtering');
      return false;
    }

    const allowed = getAllowedRingSizes();
    if (!allowed.length) {
      log('No allowed sizes configured - showing all options');
      return false;
    }

    // Wait for options to be populated by Infinite Options
    const options = Array.from(select.options);
    const nonPlaceholderOptions = options.filter(opt => opt.value !== '' && !opt.disabled);

    if (nonPlaceholderOptions.length === 0) {
      log('No options loaded yet in dropdown, will retry');
      return false;
    }

    log('Current options count:', options.length);
    log('Allowed sizes:', allowed);

    const normalizedAllowed = new Set(allowed.map((v) => normalizeSize(v)));

    // Check if any options match the allowed list
    const hasMatch = options.some((opt) => {
      const value = (opt.value || opt.textContent || '').trim();
      if (value === '' || opt.disabled) return false;
      return normalizedAllowed.has(normalizeSize(value));
    });

    if (!hasMatch) {
      log('Warning: No options match the allowed sizes - keeping all options to prevent empty dropdown');
      return false;
    }

    let removedCount = 0;
    options.forEach((opt) => {
      const value = (opt.value || opt.textContent || '').trim();
      if (value === '' || opt.disabled) return; // keep placeholder/disabled
      if (!normalizedAllowed.has(normalizeSize(value))) {
        opt.remove();
        removedCount++;
      }
    });

    log('Removed', removedCount, 'options. Remaining:', select.options.length);

    // If current value is no longer valid, reset to placeholder or first allowed
    if (!normalizedAllowed.has(normalizeSize(select.value || ''))) {
      const placeholder = select.querySelector(`option[value="${PLACEHOLDER_VALUE}"]`);
      if (placeholder) {
        select.value = PLACEHOLDER_VALUE;
      } else {
        const firstAllowed = Array.from(select.options).find((opt) => normalizedAllowed.has(normalizeSize((opt.value || opt.textContent || '').trim())));
        if (firstAllowed) select.value = firstAllowed.value;
      }
    }

    hasFiltered = true;
    return true;
  }

  // Function to validate on form submission
  function validateRingSize(event) {
    const select = getRingSizeSelect();
    if (!select) return false; // No select found, don't block

    if (!isRingSizeSelected()) {
      showError(RING_SIZE_ERROR_MESSAGE);
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return true; // Validation failed
    }

    hideError();
    return false; // Validation passed
  }

  // Function to check if this is an add to cart form
  function shouldValidateForm(form) {
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return false;
    if (form.closest('.cart-drawer') || form.classList.contains('cart-drawer__form')) return false;
    if (!getRingSizeSelect()) return false;
    return true;
  }

  // Store original options for re-filtering on variant change
  let originalOptions = null;

  // Cache original dropdown options before any filtering
  function cacheOriginalOptions() {
    const select = getRingSizeSelect();
    if (!select || originalOptions) return;

    originalOptions = Array.from(select.options).map(opt => ({
      value: opt.value,
      text: opt.text,
      disabled: opt.disabled,
      selected: opt.selected
    }));
    log('Cached', originalOptions.length, 'original options');
  }

  // Restore original options before re-filtering
  function restoreOriginalOptions() {
    const select = getRingSizeSelect();
    if (!select || !originalOptions) return;

    // Clear current options
    select.innerHTML = '';

    // Restore from cache
    originalOptions.forEach(optData => {
      const opt = document.createElement('option');
      opt.value = optData.value;
      opt.text = optData.text;
      opt.disabled = optData.disabled;
      select.appendChild(opt);
    });

    log('Restored', originalOptions.length, 'original options');
  }

  // Update filter sizes when variant changes
  function updateSizesForVariant(variantId) {
    if (!window.productRingSizeFilter) return;

    const variantSizes = window.productRingSizeFilter.variantSizes;
    if (!variantSizes) return;

    const newSizes = variantSizes[variantId];

    if (newSizes && newSizes.length > 0) {
      window.productRingSizeFilter.sizes = newSizes;
      window.productRingSizeFilter.source = 'variant';
      log('Updated sizes for variant', variantId, ':', newSizes);
    } else if (window.productRingSizeFilter.productSizes) {
      // Fall back to product-level sizes
      window.productRingSizeFilter.sizes = window.productRingSizeFilter.productSizes;
      window.productRingSizeFilter.source = 'product';
      log('Using product-level sizes for variant', variantId);
    }

    // Re-filter the dropdown
    hasFiltered = false;
    restoreOriginalOptions();
    addPlaceholderOption();
    filterRingSizeOptions();
  }

  // Initialize the script
  function init(retryCount = 0) {
    const MAX_RETRIES = 20;
    const RETRY_DELAY = 300;

    const select = getRingSizeSelect();
    if (!select) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => init(retryCount + 1), RETRY_DELAY);
      }
      return;
    }

    // Cache original options before any modifications
    cacheOriginalOptions();

    // Add the placeholder option
    addPlaceholderOption();

    // Filter available sizes based on metafield
    const filtered = filterRingSizeOptions();

    // If filtering failed (options not loaded yet), retry
    if (!filtered && retryCount < MAX_RETRIES) {
      setTimeout(() => {
        cacheOriginalOptions();
        filterRingSizeOptions();
      }, RETRY_DELAY);
    }

    // Add change listener to hide error when a size is selected
    if (!select.__ringSizeChangeListener) {
      select.__ringSizeChangeListener = true;
      select.addEventListener('change', function() {
        if (isRingSizeSelected()) {
          hideError();
        }
      });
    }
  }

  // Listen for form submissions
  document.addEventListener('submit', function(event) {
    const form = event.target;
    if (!shouldValidateForm(form)) return;
    validateRingSize(event);
  }, true);

  // Listen for add to cart button clicks (backup validation)
  document.addEventListener('click', function(event) {
    const button = event.target.closest('[name="add"], .btn-product-add, .product-form__submit, button[type="submit"], input[type="submit"]');
    if (!button) return;

    // Skip validation for sold out buttons - they redirect to a different page
    if (button.classList.contains('product-form__submit--sold-out')) return;

    const form = button.closest('form[data-product-form], form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    if (!shouldValidateForm(form)) return;

    validateRingSize(event);
  }, true);

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Set up a mutation observer to handle dynamic content changes
  const observer = new MutationObserver(function(mutations) {
    for (let mutation of mutations) {
      if (mutation.type === 'childList') {
        const hasInfiniteOptions = mutation.target.id === 'infiniteoptions-container' ||
                                  (mutation.target.querySelector && mutation.target.querySelector('#infiniteoptions-container'));

        if (hasInfiniteOptions) {
          setTimeout(init, 100);
          break;
        }
      }
    }
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: false
  });

  // Listen for variant changes to update ring size filter
  function setupVariantChangeListener() {
    // Listen for the theme's variant change event
    document.addEventListener('variant:change', function(event) {
      const variant = event.detail && event.detail.variant;
      if (variant && variant.id) {
        log('Variant changed to:', variant.id);
        updateSizesForVariant(variant.id);
      }
    });

    // Also listen for pub/sub variant changes (used by some themes)
    if (typeof subscribe === 'function') {
      const variantChangeEventName = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange)
        ? PUB_SUB_EVENTS.variantChange
        : 'variant-change';

      try {
        subscribe(variantChangeEventName, function(payload) {
          const variant = payload && payload.data ? payload.data.variant : undefined;
          if (variant && variant.id) {
            log('Variant changed (pub/sub) to:', variant.id);
            updateSizesForVariant(variant.id);
          }
        });
      } catch (e) {
        log('Pub/sub subscription failed:', e);
      }
    }

    // Listen for URL changes (variant is often in URL)
    let lastUrl = location.href;
    const urlObserver = new MutationObserver(function() {
      if (location.href !== lastUrl) {
        lastUrl = location.href;
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        if (variantId) {
          log('URL variant changed to:', variantId);
          setTimeout(() => updateSizesForVariant(variantId), 100);
        }
      }
    });
    urlObserver.observe(document.body, { childList: true, subtree: true });

    // Listen for variant selector changes (radio buttons or selects)
    document.addEventListener('change', function(event) {
      const target = event.target;
      if (!target) return;

      // Check if this is a variant selector
      if (target.matches('.product__variant-options input[type="radio"], .product__variant-options select, [data-product-option] input, [data-product-option] select')) {
        // Give the theme time to update the variant
        setTimeout(function() {
          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get('variant');
          if (variantId) {
            log('Variant selector changed, new variant:', variantId);
            updateSizesForVariant(variantId);
          }
        }, 150);
      }
    });
  }

  // Set up variant change listeners
  setupVariantChangeListener();

})();
